import type {ReactNode} from 'react';
import type {SelectOption} from '../../lib/selector-option';
import type {
  SelectGroupedOption,
  SelectIsOptionDisabled,
  SelectOptionGroup,
  SelectValue,
} from './types';

export function isOptionGroup<T, V extends SelectValue, L extends ReactNode>(
  item: SelectGroupedOption<T, V, L> | null | undefined,
): item is SelectOptionGroup<T, V, L> {
  if (!item || typeof item !== 'object') return false;
  if ('value' in item) return false;
  return Array.isArray((item as SelectOptionGroup<T, V, L>).options);
}

export function flattenGroupedOptions<T, V extends SelectValue, L extends ReactNode>(
  items: SelectGroupedOption<T, V, L>[] | undefined | null,
): SelectOption<T, V, L>[] {
  if (!items) return [];
  const result: SelectOption<T, V, L>[] = [];
  for (const item of items) {
    if (isOptionGroup(item)) {
      for (const option of item.options) result.push(option);
    } else {
      result.push(item);
    }
  }
  return result;
}

export function getOptionIndex<T, V extends SelectValue, L extends ReactNode>(
  options: SelectOption<T, V, L>[],
  option?: SelectOption<T, V, L> | null,
) {
  if (!option) return -1;
  return options.findIndex(item => item.value === option.value);
}

export function isOptionEnabled<T, V extends SelectValue, L extends ReactNode>(
  option: SelectOption<T, V, L> | undefined,
  isOptionDisabled?: SelectIsOptionDisabled<T, V, L>,
) {
  if (!option) return false;
  if (option.isDisabled) return false;
  if (isOptionDisabled?.(option)) return false;
  return true;
}

export function getFirstEnabledOptionIndex<T, V extends SelectValue, L extends ReactNode>(
  options: SelectOption<T, V, L>[],
  isOptionDisabled?: SelectIsOptionDisabled<T, V, L>,
) {
  return options.findIndex(option => isOptionEnabled(option, isOptionDisabled));
}

export function getLastEnabledOptionIndex<T, V extends SelectValue, L extends ReactNode>(
  options: SelectOption<T, V, L>[],
  isOptionDisabled?: SelectIsOptionDisabled<T, V, L>,
) {
  for (let index = options.length - 1; index >= 0; index -= 1) {
    if (isOptionEnabled(options[index], isOptionDisabled)) return index;
  }
  return -1;
}

export function getNextEnabledOptionIndex<T, V extends SelectValue, L extends ReactNode>(
  options: SelectOption<T, V, L>[],
  startIndex: number,
  step: 1 | -1,
  isOptionDisabled?: SelectIsOptionDisabled<T, V, L>,
) {
  let nextIndex = startIndex;
  while (nextIndex >= 0 && nextIndex < options.length) {
    if (isOptionEnabled(options[nextIndex], isOptionDisabled)) return nextIndex;
    nextIndex += step;
  }
  return -1;
}

export function defaultFilterOption<T, V extends SelectValue, L extends ReactNode>(
  option: SelectOption<T, V, L>,
  input: string,
) {
  if (!input) return true;
  const text = typeof option.label === 'string' ? option.label : String(option.value);
  return text.toLowerCase().includes(input.toLowerCase());
}

export function isOptionSelected<T, V extends SelectValue, L extends ReactNode>(
  option: SelectOption<T, V, L>,
  selectedValue?: SelectOption<T, V, L> | null,
  selectedValues?: SelectOption<T, V, L>[],
) {
  if (selectedValues?.length) {
    return selectedValues.some(item => item.value === option.value);
  }
  return selectedValue?.value === option.value;
}

export function defaultIsOptionSelected<T, V extends SelectValue, L extends ReactNode>(
  option: SelectOption<T, V, L>,
  selectValue: SelectOption<T, V, L>[],
) {
  return selectValue.some(item => item.value === option.value);
}

const COUNTRY_ALIASES: Array<[string, string[]]> = [['Türkiye', ['Turkey', 'Turkiye']]];

function normalizeSearchText(value: string) {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

const ALIAS_LOOKUP = COUNTRY_ALIASES.reduce(
  (acc, [officialName, aliases]) => {
    const officialLower = normalizeSearchText(officialName);
    aliases.forEach(alias => {
      acc[normalizeSearchText(alias)] = officialLower;
    });
    return acc;
  },
  {} as Record<string, string>,
);

function getOptionSearchText<T, V extends SelectValue, L extends ReactNode>(
  option: SelectOption<T, V, L>,
) {
  if (typeof option.label === 'string' || typeof option.label === 'number') {
    return String(option.label);
  }

  return String(option.value);
}

export function countriesFilter<T, V extends SelectValue, L extends ReactNode>(
  option: SelectOption<T, V, L>,
  inputValue: string,
): boolean {
  const input = normalizeSearchText(inputValue.trim());

  if (!input) {
    return true;
  }

  const label = normalizeSearchText(getOptionSearchText(option));

  if (label.includes(input)) {
    return true;
  }

  const officialName = ALIAS_LOOKUP[input];

  if (officialName && label.includes(officialName)) {
    return true;
  }

  for (const [alias, official] of Object.entries(ALIAS_LOOKUP)) {
    if (alias.includes(input) && label.includes(official)) {
      return true;
    }
  }

  return false;
}
